/**
 * Update-category cell rendering for RowTable — extracted so RowTable.tsx can
 * stay focused on row-grouping/table-layout concerns.
 *
 * - getPropertyChange: what an update row's value will do to the existing
 *   record (conflict / added / unmarked). Rendered by UpdateValueCell, its
 *   own sibling file.
 * - renderRowTableCell: dispatches a RowTable column to its cell renderer
 *   (error / record-chip / conflict badges / count badge / update value /
 *   plain value).
 *
 * The mobile-card counterpart (UpdateRowFooter) and the update-value cell
 * component (UpdateValueCell) each live in their own sibling file —
 * react-refresh forbids a component declaration, exported or not, sharing a
 * file with the non-component exports here.
 */

import { Badge } from '@/lib/base/badges/badges';
import { Table } from '@/lib/base/table';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { ConflictBadgeList } from './ConflictBadgeList';
import type { RowCategory, RowData } from './types';
import { UpdateValueCell } from './UpdateValueCell';

/** Per row id, the properties that conflict — keyed `${file_id}-${row_index}`. */
export type ConflictsByRow = Record<string, Set<string>>;

/** Stable shared fallback for rows with no conflicts — avoids a fresh array
 *  reference on every render/cell-invocation for the common no-conflict case. */
export const EMPTY_CONFLICTS: string[] = [];

/**
 * What an update will do to one property of the existing record.
 *
 * - `conflict` — both sides hold a value and they disagree. Taken from the
 *   analyzer's own conflict list, never recomputed here: the CSV value is raw
 *   ("+14155550101") while the CRM value is normalized ({country_code, …}), so
 *   a client-side comparison would call every phone, date and currency cell
 *   changed. That is issue #9928, and re-deriving it here would reintroduce it
 *   in the UI after it was fixed in the analyzer.
 * - `added` — the record has no value for this property yet. Absence needs no
 *   normalization to detect, so this one IS safe to derive here.
 *
 * A property with neither marker is left alone: it may be unchanged, and
 * without a shared normalizer the honest answer is "we're not claiming".
 */
export type PropertyChange = 'conflict' | 'added';

/** What this row's value for `propertyName` will do to the existing record. */
function getPropertyChange(
  row: RowData,
  propertyName: string,
  conflictsByRow: ConflictsByRow | undefined,
  existingObjectsMap: Record<string, CrmObject> | undefined
): PropertyChange | undefined {
  if (conflictsByRow?.[row.id]?.has(propertyName)) {
    return 'conflict';
  }
  const existing = row.existing_entity_id
    ? existingObjectsMap?.[row.existing_entity_id]
    : undefined;
  if (!existing) {
    return undefined;
  }
  // Only claim "added" when the record genuinely holds nothing here. Anything
  // else is left unmarked rather than guessed — see PropertyChange.
  const current = getPropertyValue<unknown>(
    existing.properties?.[propertyName]
  );
  const isEmpty =
    current === undefined ||
    current === null ||
    current === '' ||
    (Array.isArray(current) && current.length === 0);
  return isEmpty ? 'added' : undefined;
}

export interface ColumnDef {
  id: string;
  label: string;
}

/** Grouped row used by the table body. Members share the same dedup key. */
export interface RowGroup {
  /** Stable React key + identity for navigation. */
  key: string;
  /** Display data uses the first row that landed in this group. */
  sample: RowData;
  /** All underlying row ids in this group — used so navigation focus from
   *  any of them resolves to this group. */
  memberRowIds: string[];
  /** 1-indexed source row numbers for the "Rows 1, 3, 7" hint. */
  memberRowNumbers: number[];
}

export const COUNT_COLUMN_ID = '__count__';
export const ERROR_COLUMN_ID = '__error__';
/** Update rows only: the existing CRM record this row will change. */
export const RECORD_COLUMN_ID = '__record__';
/** Update rows only: flags rows whose values disagree with the existing record. */
export const CONFLICT_COLUMN_ID = '__conflict__';
/** Prefix for per-label association columns (e.g. "Linked Company"). */
export const ASSOC_COLUMN_PREFIX = '__assoc__:';
export const ASSOCIATION_UNRESOLVED_HINT = 'Not in this import';
export const ASSOCIATION_COLUMN_TOOLTIP =
  'Relationships this import will create between records.';
/** Character cap for property values in review grid cells. Long text (e.g.
 *  AI-generated status paragraphs from a source CRM) otherwise balloons row
 *  height and buries every other column. PropertyValueLabel renders an inline
 *  "more/less" toggle past this cap, so the full value stays reachable. */
export const REVIEW_CELL_MAX_LENGTH = 160;

/** Native-tooltip-friendly text listing the source CSV row numbers in a group. */
function formatGroupRowsTooltip(rowNumbers: number[]): string {
  if (rowNumbers.length === 0) {
    return '';
  }
  if (rowNumbers.length === 1) {
    return `Row ${rowNumbers[0]}`;
  }
  if (rowNumbers.length <= 8) {
    return `Rows ${rowNumbers.join(', ')}`;
  }
  const head = rowNumbers.slice(0, 6).join(', ');
  return `Rows ${head} + ${rowNumbers.length - 6} more`;
}

export interface RowTableCellProps {
  column: ColumnDef;
  group: RowGroup;
  category: RowCategory;
  schemaMap: PropertySchemaMap;
  newPropertyLabels: Record<string, string>;
  orgId: number;
  conflictsByRow?: ConflictsByRow;
  existingObjectsMap?: Record<string, CrmObject>;
}

/** Renders the cell for one column of a RowTable row — dispatches by column id. */
export function renderRowTableCell({
  column,
  group,
  category,
  schemaMap,
  newPropertyLabels,
  orgId,
  conflictsByRow,
  existingObjectsMap,
}: RowTableCellProps) {
  if (column.id.startsWith(ASSOC_COLUMN_PREFIX)) {
    const assocLabel = column.id.slice(ASSOC_COLUMN_PREFIX.length);
    const entries = (group.sample.associationDisplay ?? []).filter(
      a => a.label === assocLabel
    );
    if (entries.length === 0) {
      return (
        <Table.Cell id={column.id} className='text-fg-quaternary'>
          —
        </Table.Cell>
      );
    }
    const resolvedEntries = entries.filter(a => a.resolved);
    if (resolvedEntries.length > 0) {
      return (
        <Table.Cell id={column.id}>
          {resolvedEntries.map(a => a.value).join(', ')}
        </Table.Cell>
      );
    }
    return (
      <Table.Cell id={column.id}>
        {entries[0].value}
        <span className='text-fg-quaternary'>
          {' '}
          ({ASSOCIATION_UNRESOLVED_HINT})
        </span>
      </Table.Cell>
    );
  }
  if (column.id === ERROR_COLUMN_ID) {
    return (
      <Table.Cell
        id={column.id}
        className='min-w-[280px] text-utility-error-700 whitespace-normal break-words'
      >
        {group.sample.error || '—'}
      </Table.Cell>
    );
  }
  if (column.id === RECORD_COLUMN_ID) {
    // EntityBadgeById, not the EntityBadge primitive: the
    // wrapper is the layer that owns click behaviour, so
    // this opens the record's slideout. The primitive is
    // deliberately inert, which is why the chip did nothing.
    return (
      <Table.Cell id={column.id}>
        {group.sample.existing_entity_id ? (
          <EntityBadgeById
            entityId={group.sample.existing_entity_id}
            orgId={orgId}
            variant='xs'
          />
        ) : (
          <span className='text-fg-quaternary'>—</span>
        )}
      </Table.Cell>
    );
  }
  if (column.id === CONFLICT_COLUMN_ID) {
    // Name the properties rather than just flagging the
    // row: a bare "Conflict" badge makes the user open the
    // resolver to discover which field it even refers to.
    const conflictSet = conflictsByRow?.[group.sample.id];
    const conflicting =
      conflictSet && conflictSet.size > 0 ? [...conflictSet] : EMPTY_CONFLICTS;
    return (
      <Table.Cell id={column.id}>
        {conflicting.length > 0 ? (
          <span className='flex flex-wrap items-center gap-1'>
            <ConflictBadgeList
              conflictingProperties={conflicting}
              schemaMap={schemaMap}
              newPropertyLabels={newPropertyLabels}
            />
          </span>
        ) : (
          <span className='text-fg-quaternary'>—</span>
        )}
      </Table.Cell>
    );
  }
  if (column.id === COUNT_COLUMN_ID) {
    const count = group.memberRowIds.length;
    if (count <= 1) {
      return (
        <Table.Cell id={column.id} className='tabular-nums text-fg-quaternary'>
          1
        </Table.Cell>
      );
    }
    return (
      <Table.Cell id={column.id} className='tabular-nums'>
        <span
          className='inline-flex'
          title={formatGroupRowsTooltip(group.memberRowNumbers)}
        >
          <Badge color='gray' size='sm'>
            {`×${count}`}
          </Badge>
        </span>
      </Table.Cell>
    );
  }
  if (category === 'update') {
    return (
      <Table.Cell id={column.id}>
        <UpdateValueCell
          value={group.sample.data[column.id]}
          propertyName={column.id}
          propertyType={schemaMap[column.id]}
          change={getPropertyChange(
            group.sample,
            column.id,
            conflictsByRow,
            existingObjectsMap
          )}
          existingObject={
            group.sample.existing_entity_id
              ? existingObjectsMap?.[group.sample.existing_entity_id]
              : undefined
          }
        />
      </Table.Cell>
    );
  }
  return (
    <Table.Cell id={column.id}>
      <PropertyValueLabel
        value={group.sample.data[column.id]}
        propertyType={schemaMap[column.id]}
        fontSize='sm'
        maxLength={REVIEW_CELL_MAX_LENGTH}
      />
    </Table.Cell>
  );
}
